import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

export default class ReorderController extends Controller {
  static targets = ['item'];
  static values = {
    url: String,
    paramName: { type: String, default: 'ordered_ids[]' },
  };

  declare readonly itemTargets: HTMLElement[];
  declare readonly urlValue: string;
  declare readonly paramNameValue: string;

  private draggingItem: HTMLElement | null = null;

  dragStart(event: DragEvent) {
    const item = event.currentTarget as HTMLElement;
    this.draggingItem = item;

    event.dataTransfer?.setData('text/plain', item.dataset.reorderId ?? '');
    event.dataTransfer?.setDragImage(item, 20, 20);

    item.classList.add('opacity-60');
  }

  dragOver(event: DragEvent) {
    event.preventDefault();

    const targetItem = event.currentTarget as HTMLElement;
    if (!this.draggingItem || targetItem === this.draggingItem) return;

    const targetRect = targetItem.getBoundingClientRect();
    const insertBefore = event.clientY < targetRect.top + targetRect.height / 2;

    const parent = targetItem.parentElement;
    if (!parent) return;

    if (insertBefore) {
      parent.insertBefore(this.draggingItem, targetItem);
    } else {
      parent.insertBefore(this.draggingItem, targetItem.nextSibling);
    }
  }

  drop(event: DragEvent) {
    event.preventDefault();
    void this.persistOrder();
  }

  dragEnd() {
    if (this.draggingItem) {
      this.draggingItem.classList.remove('opacity-60');
    }

    this.draggingItem = null;
  }

  private orderedIds(): string[] {
    return this.itemTargets
      .map((item) => item.dataset.reorderId)
      .filter((id): id is string => Boolean(id));
  }

  private csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  private async persistOrder() {
    const orderedIds = this.orderedIds();
    if (orderedIds.length === 0) return;

    const body = new URLSearchParams();
    orderedIds.forEach((id) => body.append(this.paramNameValue, id));

    const response = await fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfToken(),
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: body.toString(),
      credentials: 'same-origin',
    });

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn('Failed to persist reorder', response.status);
    }
  }
}

application.register('ui--reorder', ReorderController);
